import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Palette,
  Code2,
  ScrollText,
  Save,
  Loader2,
  Copy,
  CheckCircle2,
  Upload,
  AlertTriangle,
  Monitor,
  Smartphone,
  ExternalLink,
  Globe,
  Plus,
  Trash2,
  Mail,
  HelpCircle,
  RotateCw,
} from "lucide-react";
import api from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FormShell } from "../components/RgpdForm";
import { POPULATION_CATEGORIES, RIGHT_TYPES } from "../i18n";
import { isLowContrast, contrastRatio, readableForeground } from "../lib/color";

const STYLES = [
  { id: "sober", label: "Sobre standard" },
  { id: "corporate", label: "Corporate Shield" },
  { id: "crisp", label: "Crisp Minimal" },
  { id: "soft", label: "Doux arrondi" },
  { id: "banner", label: "Bandeau de marque" },
];

const TABS = [
  { id: "branding", label: "Branding", icon: Palette },
  { id: "embed", label: "Intégration", icon: Code2 },
  { id: "notifications", label: "Notifications", icon: Mail },
  { id: "traces", label: "Traces techniques", icon: ScrollText },
];

function ColorField({ id, label, value, onChange, testid }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 cursor-pointer rounded border border-slate-300 bg-white p-1"
          data-testid={testid}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-32 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
          aria-label={`${label} hex`}
        />
      </div>
    </div>
  );
}

export default function OrgEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [branding, setBranding] = useState(null);
  const [tab, setTab] = useState("branding");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [logsData, setLogsData] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [previewLang, setPreviewLang] = useState("fr");
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [savingDomains, setSavingDomains] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [senderName, setSenderName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [retrying, setRetrying] = useState(null);

  useEffect(() => {
    api.get(`/admin/organizations/${id}`).then((r) => {
      setOrg(r.data);
      setBranding(r.data.branding);
      setDomains(r.data.allowed_domains || []);
      setSenderName(r.data.ack_email_sender_name || "");
      setReplyTo(r.data.ack_email_reply_to || "");
      setPreviewLang(r.data.branding?.default_language || "fr");
    });
  }, [id]);

  useEffect(() => {
    if (tab === "traces") {
      api.get(`/admin/organizations/${id}/logs`).then((r) => setLogsData(r.data));
    }
  }, [tab, id]);

  const previewConfig = useMemo(
    () =>
      org && {
        org_name: org.name,
        public_token: org.public_token,
        form_version: "v1",
        right_types: RIGHT_TYPES,
        population_categories: POPULATION_CATEGORIES,
        branding,
        limits: {},
      },
    [org, branding]
  );

  const origin = window.location.origin;
  const iframeSnippet = org
    ? `<iframe\n  src="${origin}/embed/${org.public_token}"\n  title="Formulaire RGPD"\n  style="width:100%;border:0;min-height:900px"\n  loading="lazy">\n</iframe>`
    : "";
  const loaderSnippet = org
    ? `<div id="rgpd-form"></div>\n<script\n  src="${origin}/loader.js"\n  data-rgpd-token="${org.public_token}"\n  data-rgpd-target="rgpd-form"\n  async>\n</script>`
    : "";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/organizations/${id}/branding`, branding);
      setOrg(data);
      toast.success("Branding enregistré");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    setPublishing(true);
    try {
      const url = org.published ? "unpublish" : "publish";
      const { data } = await api.post(`/admin/organizations/${id}/${url}`);
      setOrg(data);
      toast.success(data.published ? "Configuration publiée" : "Configuration dépubliée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opération impossible");
    } finally {
      setPublishing(false);
    }
  };

  const retryDelivery = async (log) => {
    setRetrying(log.id);
    try {
      const { data } = await api.post(`/admin/organizations/${id}/logs/${log.id}/retry`);
      setLogsData((d) => ({ ...d, logs: d.logs.map((l) => (l.id === data.id ? data : l)) }));
      if (data.delivery_status === "delivered") toast.success(`${log.reference_id} transmise`);
      else if (data.delivery_status === "queued") toast.info(`${log.reference_id} remise en file d'attente`);
      else toast.error(`Nouvel échec pour ${log.reference_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Relance impossible");
      api.get(`/admin/organizations/${id}/logs`).then((r) => setLogsData(r.data));
    } finally {
      setRetrying(null);
    }
  };

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 512 * 1024) {
      toast.error("Logo trop volumineux (max 512 Ko)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBranding((b) => ({ ...b, logo_url: String(reader.result) }));
    reader.readAsDataURL(f);
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié`);
  };

  const cleanDomain = (v) => {
    let s = (v || "").trim().toLowerCase();
    if (s.includes("//")) s = s.split("//")[1];
    s = s.split("/")[0].split(":")[0];
    if (s.startsWith("www.")) s = s.slice(4);
    return s;
  };

  const saveDomains = async (list) => {
    setSavingDomains(true);
    try {
      const { data } = await api.put(`/admin/organizations/${id}/allowed-domains`, { domains: list });
      setOrg(data);
      setDomains(data.allowed_domains || []);
      toast.success("Domaines autorisés mis à jour");
    } catch {
      toast.error("Échec de la mise à jour");
    } finally {
      setSavingDomains(false);
    }
  };

  const addDomain = () => {
    const d = cleanDomain(newDomain);
    if (!d) return;
    if (domains.includes(d)) {
      toast.error("Domaine déjà présent");
      return;
    }
    const next = [...domains, d];
    setNewDomain("");
    saveDomains(next);
  };

  const removeDomain = (d) => saveDomains(domains.filter((x) => x !== d));

  const saveNotifications = async (patch, successMessage) => {
    setSavingNotifications(true);
    try {
      const { data } = await api.put(`/admin/organizations/${id}/notifications`, patch);
      setOrg(data);
      setSenderName(data.ack_email_sender_name || "");
      setReplyTo(data.ack_email_reply_to || "");
      toast.success(successMessage);
    } catch (e) {
      toast.error(e.response?.status === 422 ? "Nom d'expéditeur ou adresse de réponse invalide" : "Échec de la mise à jour");
    } finally {
      setSavingNotifications(false);
    }
  };

  const toggleAckEmail = (enabled) =>
    saveNotifications(
      { ack_email_enabled: enabled },
      enabled ? "Accusé de réception activé" : "Accusé de réception désactivé"
    );

  const saveSender = (e) => {
    e.preventDefault();
    saveNotifications(
      { ack_email_sender_name: senderName.trim(), ack_email_reply_to: replyTo.trim() },
      "Expéditeur mis à jour"
    );
  };

  if (!org || !branding) {
    return (
      <AdminLayout title="Chargement…">
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  const lowPrimary = isLowContrast(branding.primary_color);

  return (
    <AdminLayout
      title={org.name}
      subtitle={
        <span className="font-mono text-xs">
          {org.public_token} · Moovapps : {org.saas_org_id || "non renseigné"} · {org.published ? "publié" : "brouillon"}
        </span>
      }
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
          <button
            onClick={togglePublish}
            disabled={publishing}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
              org.published ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"
            }`}
            data-testid="publish-toggle-switch"
          >
            {publishing && <Loader2 className="h-4 w-4 animate-spin" />}
            {org.published ? "Dépublier" : "Publier"}
          </button>
        </div>
      }
    >
      {/* Tabs */}
      <div className="mb-6 flex gap-1 border-b border-slate-200">
        {TABS.map((tItem) => {
          const Icon = tItem.icon;
          return (
            <button
              key={tItem.id}
              onClick={() => setTab(tItem.id)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === tItem.id
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${tItem.id}`}
            >
              <Icon className="h-4 w-4" /> {tItem.label}
            </button>
          );
        })}
      </div>

      {tab === "branding" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Editor */}
          <div className="space-y-5 rounded-xl border border-slate-300 bg-white p-6" data-testid="branding-editor">
            <ColorField
              id="primary-color"
              label="Couleur primaire"
              value={branding.primary_color}
              onChange={(v) => setBranding({ ...branding, primary_color: v })}
              testid="branding-primary-color-picker"
            />
            {lowPrimary && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800" data-testid="contrast-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  Contraste insuffisant (WCAG AA). Le texte sera automatiquement forcé en{" "}
                  {readableForeground(branding.primary_color) === "#FFFFFF" ? "blanc" : "noir"} pour rester lisible.
                </span>
              </div>
            )}
            <ColorField
              id="secondary-color"
              label="Couleur secondaire"
              value={branding.secondary_color}
              onChange={(v) => setBranding({ ...branding, secondary_color: v })}
              testid="branding-secondary-color-picker"
            />

            <div>
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Logo</span>
              <div className="flex items-center gap-3">
                <span
                  className="flex h-14 w-14 items-center justify-center rounded-lg border border-slate-200"
                  style={{ background: branding.primary_color }}
                >
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Logo" className="h-10 w-10 rounded object-contain" />
                  ) : (
                    <span className="text-xs text-white/70">—</span>
                  )}
                </span>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <Upload className="h-4 w-4" /> Charger un logo
                  <input type="file" accept="image/*" className="sr-only" onChange={onLogo} data-testid="branding-logo-upload" />
                </label>
                {branding.logo_url && (
                  <button
                    onClick={() => setBranding({ ...branding, logo_url: "" })}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Retirer
                  </button>
                )}
              </div>
            </div>

            <div>
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Style prédéfini</span>
              <div className="flex flex-wrap gap-2">
                {STYLES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setBranding({ ...branding, style: s.id })}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      branding.style === s.id
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`style-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="default-lang" className="mb-1.5 block text-sm font-medium text-slate-700">
                Langue par défaut
              </label>
              <select
                id="default-lang"
                value={branding.default_language}
                onChange={(e) => setBranding({ ...branding, default_language: e.target.value })}
                className="w-40 rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                data-testid="branding-default-language"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>

            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
              data-testid="branding-save-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Enregistrer le branding
            </button>
          </div>

          {/* Live preview */}
          <div className="rounded-xl border border-slate-300 bg-slate-100 p-4" data-testid="live-preview">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Prévisualisation</span>
              <div className="flex items-center gap-2">
                <div className="flex rounded-md border border-slate-300 bg-white p-0.5">
                  {["fr", "en"].map((l) => (
                    <button
                      key={l}
                      onClick={() => setPreviewLang(l)}
                      className={`rounded px-2 py-1 text-xs font-semibold uppercase ${
                        previewLang === l ? "bg-slate-900 text-white" : "text-slate-500"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <div className="flex rounded-md border border-slate-300 bg-white p-0.5">
                  <button
                    onClick={() => setPreviewDevice("desktop")}
                    className={`rounded p-1.5 ${previewDevice === "desktop" ? "bg-slate-900 text-white" : "text-slate-500"}`}
                    aria-label="Aperçu bureau"
                    data-testid="preview-desktop"
                  >
                    <Monitor className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice("mobile")}
                    className={`rounded p-1.5 ${previewDevice === "mobile" ? "bg-slate-900 text-white" : "text-slate-500"}`}
                    aria-label="Aperçu mobile"
                    data-testid="preview-mobile"
                  >
                    <Smartphone className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto rounded-lg bg-white">
              <div className={previewDevice === "mobile" ? "mx-auto w-[375px]" : "w-full"}>
                <div className="pointer-events-none scale-100">
                  <FormShell
                    config={previewConfig}
                    lang={previewLang}
                    onLangChange={setPreviewLang}
                    preview
                    embed
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "embed" && (
        <div className="max-w-3xl space-y-6">
          <div className="rounded-xl border border-slate-300 bg-white p-6" data-testid="allowed-domains-card">
            <div className="mb-1 flex items-center gap-2">
              <Globe className="h-4 w-4 text-slate-500" />
              <h3 className="font-display text-base font-semibold text-slate-900">Domaines d'intégration autorisés</h3>
            </div>
            <p className="mb-4 text-sm text-slate-500">
              Restreignez les sites autorisés à embarquer ce formulaire (iframe / script loader). Laissez vide pour
              autoriser tous les domaines.
            </p>
            <div className="flex gap-2">
              <input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addDomain();
                  }
                }}
                placeholder="exemple.com"
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                data-testid="allowed-domain-input"
              />
              <button
                onClick={addDomain}
                disabled={savingDomains}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
                data-testid="add-domain-button"
              >
                {savingDomains ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ajouter
              </button>
            </div>
            <div className="mt-4">
              {domains.length === 0 ? (
                <p className="rounded-md bg-slate-50 px-3 py-2.5 text-sm text-slate-500" data-testid="domains-empty">
                  Aucune restriction — tous les domaines sont autorisés.
                </p>
              ) : (
                <ul className="flex flex-wrap gap-2" data-testid="allowed-domains-list">
                  {domains.map((d) => (
                    <li
                      key={d}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1 pl-3 pr-1.5 text-sm text-slate-700"
                      data-testid={`domain-chip-${d}`}
                    >
                      <span className="font-mono text-xs">{d}</span>
                      <button
                        onClick={() => removeDomain(d)}
                        className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-red-100 hover:text-red-600"
                        aria-label={`Retirer ${d}`}
                        data-testid={`remove-domain-${d}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-300 bg-white p-6">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-base font-semibold text-slate-900">URL publique</h3>
              <a
                href={`/form/${org.public_token}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
                data-testid="open-public-form"
              >
                Ouvrir <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
            <div className="flex items-center gap-2 rounded-md bg-slate-50 p-3">
              <code className="flex-1 truncate font-mono text-sm text-slate-700">{`${origin}/form/${org.public_token}`}</code>
              <button
                onClick={() => copy(`${origin}/form/${org.public_token}`, "Lien")}
                className="rounded p-1.5 text-slate-500 hover:bg-slate-200"
                aria-label="Copier le lien"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>

          {[
            { title: "Intégration iframe", code: iframeSnippet, testid: "embed-iframe" },
            { title: "Script loader (recommandé)", code: loaderSnippet, testid: "embed-loader" },
          ].map((block) => (
            <div key={block.title} className="rounded-xl border border-slate-300 bg-white p-6">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-base font-semibold text-slate-900">{block.title}</h3>
                <button
                  onClick={() => copy(block.code, "Snippet")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  data-testid={`${block.testid}-copy-button`}
                >
                  <Copy className="h-3.5 w-3.5" /> Copier
                </button>
              </div>
              <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 font-mono text-xs leading-relaxed text-slate-100" data-testid={block.testid}>
                {block.code}
              </pre>
            </div>
          ))}
          {!org.published && (
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4" /> Le formulaire n'est pas publié : les intégrations renverront une erreur tant que la configuration n'est pas publiée.
            </div>
          )}
        </div>
      )}

      {tab === "notifications" && (
        <div className="max-w-3xl">
          <div className="rounded-xl border border-slate-300 bg-white p-6" data-testid="notifications-card">
            <div className="flex items-start justify-between gap-6">
              <div>
                <label htmlFor="ack-email-toggle" className="font-display text-base font-semibold text-slate-900">
                  Accusé de réception par e-mail
                </label>
                <p id="ack-email-help" className="mt-1 text-sm text-slate-500">
                  Envoie au demandeur un e-mail reprenant la référence de suivi affichée après la soumission. Le
                  message ne contient ni le nom ni le contenu de la demande.
                </p>
              </div>
              <Switch
                id="ack-email-toggle"
                checked={!!org.ack_email_enabled}
                onCheckedChange={toggleAckEmail}
                disabled={savingNotifications}
                aria-describedby="ack-email-help"
                data-testid="ack-email-toggle-switch"
              />
            </div>

            <form onSubmit={saveSender} className="mt-6 space-y-4 border-t border-slate-200 pt-6" data-testid="ack-email-sender-form">
              <p className="text-sm text-slate-500">
                L'envoi est assuré techniquement par DataComplyOne, mais le demandeur voit le nom de votre
                organisation et ses réponses vous parviennent directement.
              </p>
              <div>
                <label htmlFor="ack-email-sender-name" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Nom d'expéditeur affiché
                </label>
                <input
                  id="ack-email-sender-name"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  maxLength={64}
                  placeholder={org.name}
                  aria-describedby="ack-email-sender-name-help"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                  data-testid="ack-email-sender-name-input"
                />
                <p id="ack-email-sender-name-help" className="mt-1 text-xs text-slate-500">
                  Vide : le nom de l'organisation est utilisé. Adresse technique d'envoi inchangée. Caractères
                  @ &lt; &gt; " interdits.
                </p>
              </div>
              <div>
                <label htmlFor="ack-email-reply-to" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Adresse de réponse (Reply-To)
                </label>
                <input
                  id="ack-email-reply-to"
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder="dpo@votre-organisation.com"
                  aria-describedby="ack-email-reply-to-help"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                  data-testid="ack-email-reply-to-input"
                />
                <p id="ack-email-reply-to-help" className="mt-1 text-xs text-slate-500">
                  Vide : le message indique au demandeur de ne pas répondre.
                </p>
              </div>
              <button
                type="submit"
                disabled={savingNotifications}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
                data-testid="ack-email-sender-save-button"
              >
                {savingNotifications ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer l'expéditeur
              </button>
            </form>
          </div>
        </div>
      )}

      {tab === "traces" && (
        <div className="rounded-xl border border-slate-300 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <h3 className="font-display text-base font-semibold text-slate-900">Journal technique</h3>
            {logsData && (
              <span className="text-xs text-slate-500">
                File d'attente : <span className="font-semibold">{logsData.pending_queue}</span>
              </span>
            )}
          </div>
          {!logsData ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : logsData.logs.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-500">Aucune trace pour le moment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="technical-traces-table">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 font-semibold">Date</th>
                    <th className="px-5 py-3 font-semibold">Référence</th>
                    <th className="px-5 py-3 font-semibold">Droit</th>
                    <th className="px-5 py-3 font-semibold">Langue</th>
                    <th className="px-5 py-3 font-semibold">Mode</th>
                    <th className="px-5 py-3 font-semibold">Statut envoi</th>
                    <th className="px-5 py-3 font-semibold">Essais</th>
                    <th className="px-5 py-3 font-semibold">Réf. SaaS</th>
                    <th className="px-5 py-3 font-semibold">Hash</th>
                    <th className="px-5 py-3 font-semibold">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logsData.logs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100 last:border-0" data-testid={`trace-row-${log.reference_id}`}>
                      <td className="whitespace-nowrap px-5 py-3 text-slate-600">
                        {new Date(log.created_at).toLocaleString("fr-FR")}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-700">{log.reference_id}</td>
                      <td className="px-5 py-3 text-slate-700">{log.right_type}</td>
                      <td className="px-5 py-3 uppercase text-slate-500">{log.language}</td>
                      <td className="px-5 py-3 text-slate-500">{log.integration_mode}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5">
                          <StatusBadge status={log.delivery_status} />
                          {["failed", "queued"].includes(log.delivery_status) && log.last_error && (
                            <Popover>
                              <PopoverTrigger
                                className="rounded-full text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                                aria-label={`Détails de l'erreur pour ${log.reference_id}`}
                                data-testid={`trace-error-details-${log.reference_id}`}
                              >
                                <HelpCircle className="h-4 w-4" />
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-96 text-sm">
                                <p className="mb-1.5 font-semibold text-slate-900">Dernière erreur d'envoi</p>
                                <pre
                                  className="whitespace-pre-wrap break-all rounded bg-slate-50 p-2 font-mono text-xs text-slate-700"
                                  data-testid={`trace-error-text-${log.reference_id}`}
                                >
                                  {log.last_error}
                                </pre>
                                {log.updated_at && (
                                  <p className="mt-2 text-xs text-slate-500">
                                    {new Date(log.updated_at).toLocaleString("fr-FR")}
                                  </p>
                                )}
                              </PopoverContent>
                            </Popover>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-600">{log.retries}</td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-700" data-testid={`trace-saas-ref-${log.reference_id}`}>
                        {log.saas_ref || "—"}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-400">{log.correlation_hash}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-right">
                        {log.retryable ? (
                          <button
                            onClick={() => retryDelivery(log)}
                            disabled={retrying === log.id}
                            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:opacity-60"
                            data-testid={`trace-retry-button-${log.reference_id}`}
                          >
                            {retrying === log.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCw className="h-3.5 w-3.5" />
                            )}
                            Relancer
                          </button>
                        ) : (
                          log.delivery_status === "failed" && (
                            <span className="text-xs text-slate-400" title="Délai de rétention dépassé : contenu purgé">
                              Données purgées
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function StatusBadge({ status }) {
  const map = {
    delivered: { label: "Transmis", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
    queued: { label: "En file", cls: "bg-amber-100 text-amber-700", icon: Loader2 },
    pending: { label: "En cours", cls: "bg-slate-100 text-slate-600", icon: Loader2 },
    failed: { label: "Échec", cls: "bg-red-100 text-red-700", icon: AlertTriangle },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}
